import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { paymentStatus, API } from "../lib/api";
import { Check, Loader2, ArrowRight, Download, Sparkles } from "lucide-react";

const REDIRECT_AFTER_SEC = 5;

// SAT color palette confetti — Gold, Royal Violet, Midnight, Ivory
const fireConfetti = () => {
  const colors = ["#D4AF37", "#a48cff", "#4B2E83", "#F7F6F2"];
  const end = Date.now() + 2200;
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 70,
      startVelocity: 55,
      origin: { x: 0, y: 0.7 },
      colors,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 70,
      startVelocity: 55,
      origin: { x: 1, y: 0.7 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Burst from center
  setTimeout(() => {
    confetti({
      particleCount: 140,
      spread: 100,
      startVelocity: 45,
      origin: { x: 0.5, y: 0.55 },
      colors,
      scalar: 1.1,
    });
  }, 250);
};

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const [countdown, setCountdown] = useState(REDIRECT_AFTER_SEC);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const downloadStartedRef = useRef(false);
  const nav = useNavigate();

  const downloadLifemap = async () => {
    setPdfError(null);
    try {
      const token = localStorage.getItem("sat_token");
      const r = await fetch(`${API}/los/lifemap/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sat-lebenslandkarte.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setPdfDownloaded(true);
    } catch (e) {
      setPdfError("Download fehlgeschlagen. Versuche es bitte erneut.");
    }
  };

  // ── Poll Stripe status ────────────────────────────────────
  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await paymentStatus(sessionId);
        if (cancelled) return;
        setData(r);
        if (r.payment_status === "paid") setStatus("paid");
        else if (r.status === "expired") setStatus("expired");
        else if (attempts >= 10) setStatus("pending_timeout");
        else setTimeout(poll, 2000);
      } catch (e) {
        if (attempts >= 10) setStatus("error");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const isLifemap = data?.product_key === "lifemap_full";

  // ── Trigger confetti + lifemap auto-download once paid ────
  useEffect(() => {
    if (status !== "paid") return;
    fireConfetti();
    if (isLifemap && !downloadStartedRef.current) {
      downloadStartedRef.current = true;
      downloadLifemap();
    }
  }, [status, isLifemap]);

  // ── Countdown + auto-redirect for lifemap ─────────────────
  useEffect(() => {
    if (status !== "paid" || !isLifemap) return;
    if (countdown <= 0) {
      nav("/los/scan");
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [status, isLifemap, countdown, nav]);

  return (
    <div className="min-h-screen pt-32 px-6 flex items-center justify-center bg-[#081328]" data-testid="checkout-success-page">
      <div className="glass rounded-2xl p-10 md:p-12 max-w-xl w-full text-center border border-white/10 bg-white/[0.03] backdrop-blur-xl">
        {status === "checking" && (<>
          <Loader2 className="mx-auto text-[#D4AF37] mb-4 animate-spin" size={40} />
          <h1 className="serif text-3xl text-white">Zahlung wird geprüft…</h1>
          <p className="text-white/55 mt-3">Einen Moment, wir bestätigen deinen Zahlungseingang.</p>
        </>)}

        {status === "paid" && !isLifemap && (<>
          <div className="mx-auto w-16 h-16 rounded-full bg-[#D4AF37] text-[#081328] flex items-center justify-center mb-4">
            <Check size={32} />
          </div>
          <h1 className="serif text-4xl text-white">Willkommen.</h1>
          <p className="text-white/65 mt-3">Deine Buchung ist bestätigt — und dein Kurs wartet auf dich.</p>
          {data?.course_slug ? (
            <Link to={`/academy/${data.course_slug}/learn`} className="btn-primary mt-8 inline-flex" data-testid="checkout-start-learning">Jetzt starten <ArrowRight size={18} /></Link>
          ) : (
            <Link to="/academy/dashboard" className="btn-primary mt-8 inline-flex">Zum Dashboard <ArrowRight size={18} /></Link>
          )}
        </>)}

        {status === "paid" && isLifemap && (<>
          <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#a48cff] text-[#081328] flex items-center justify-center mb-5 shadow-2xl shadow-[#D4AF37]/30">
            <Sparkles size={36} />
          </div>
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3">
            SAT Lebenslandkarte · Freigeschaltet
          </div>
          <h1 className="serif text-4xl md:text-5xl text-white font-light leading-tight">
            Deine Karte gehört dir.
          </h1>
          <p className="text-white/70 mt-4 text-[15px] leading-relaxed max-w-md mx-auto">
            Alle 12 Räume, alle Reflexionen, lebenslange Updates — eingebettet in ein Dokument, das nur dir gehört.
          </p>

          <div className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-xs transition" style={{
            borderColor: pdfError ? "rgba(248,113,113,0.4)" : "rgba(72,213,151,0.4)",
            background: pdfError ? "rgba(248,113,113,0.05)" : "rgba(72,213,151,0.05)",
            color: pdfError ? "#fca5a5" : "#48d597",
          }} data-testid="lifemap-download-status">
            {pdfError ? (<>{pdfError}</>) : pdfDownloaded ? (<><Check size={14} /> PDF wurde heruntergeladen</>) : (<><Loader2 size={14} className="animate-spin" /> PDF wird vorbereitet…</>)}
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            <button
              onClick={downloadLifemap}
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#D4AF37] text-[#081328] rounded-full font-semibold text-sm hover:opacity-90 transition"
              data-testid="lifemap-redownload-btn"
            >
              <Download size={14} /> {pdfError ? "Erneut versuchen" : "Erneut herunterladen"}
            </button>
            <button
              onClick={() => nav("/los/scan")}
              className="text-xs text-white/55 hover:text-[#D4AF37] tracking-wider transition"
              data-testid="lifemap-goto-now"
            >
              Jetzt zur Karte → ({countdown}s)
            </button>
          </div>

          <div className="mt-8 text-[10px] text-white/40 italic">
            Du wirst automatisch zur Lebenslandkarte weitergeleitet.
          </div>
        </>)}

        {(status === "expired" || status === "error" || status === "pending_timeout") && (<>
          <h1 className="serif text-3xl text-red-300">Hm, etwas ist schiefgelaufen.</h1>
          <p className="text-white/55 mt-3">Die Zahlung konnte (noch) nicht bestätigt werden. Bei Fragen melde dich bei uns.</p>
          <Link to="/academy" className="btn-secondary mt-8 inline-flex">Zurück zur Academy</Link>
        </>)}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
